// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface LenderData {
  lender: string;
  value: number;
  percentage: number;
}

interface LenderBarChartProps {
  data: LenderData[];
  title: string;
  color?: string;
  formatValue?: (value: number) => string;
}

// Professional color palette for lenders
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

const LenderBarChart: React.FC<LenderBarChartProps> = ({
  data,
  title,
  color = '#3b82f6',
  formatValue = (value) => `$${(value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}) => {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2">{label}</p>
          <p style={{ color: payload[0].fill }}>
            Value: {formatValue(data.value)}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {data.percentage.toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ x, y, width, value }: any) => {
    if (!Number.isFinite(value) || value === 0) return null;

    return (
      <text
        x={x + width + 5}
        y={y + 12}
        fill="#ffffff"
        fontSize={11}
        fontWeight="500"
      >
        {formatValue(value)}
      </text>
    );
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
      />

      <h3 className="font-bold text-lg mb-4 text-white relative z-10">{title}</h3>

      {data.length === 0 ? (
        <div className="p-8 bg-gray-700 rounded text-center">
          <p className="text-gray-400">No lender data available for the selected period.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(300, data.length * 50)}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 100, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
            <XAxis
              type="number"
              stroke="#8892b0"
              fontSize={12}
              tickFormatter={(value: number) => {
                if (!Number.isFinite(value)) return '$0';
                const k = Math.round(value / 100000);
                return `$${k}k`;
              }}
            />
            <YAxis
              type="category"
              dataKey="lender"
              stroke="#8892b0"
              fontSize={12}
              width={120}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Bar
              dataKey="value"
              radius={[0, 4, 4, 0]}
              label={<CustomLabel />}
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default LenderBarChart;
